import React, { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getJobStartAtDialogOpen } from 'lib/selectors/getJobStartAtDialog';
import BaseJobTimeDialog from 'components/BaseJobTimeDialog';
import { TickerContext } from 'components/TickerProvider';
import _get from 'lodash/get';
import { toISO, safeIsBefore, safeConvertToISO } from 'lib/utils/date';
import { getCompany } from 'lib/selectors/getCompany';
import { getLatestNonIdleInterval } from 'lib/selectors/getLatestNonIdleInterval';
import { getLatestOperatorRun } from 'lib/selectors/getLatestOperatorRun';
import { getLatestActivity } from 'lib/selectors/getLatestActivity';
import { actionCloseJobStartAtDialog } from 'lib/actions';
import JobStartAtDialogEventEmitter from './emitter';

/**
 * JobStartAtDialogBase Component
 *
 * A modal dialog that allows users to select when a job should start.
 * Always provides "Now" and "Last active" options, plus one additional option:
 * either "Since last job" or "Since sign in" based on which event is more recent.
 *
 * Options are automatically disabled when data is unavailable or when selecting
 * that time would create logical inconsistencies (e.g., starting before a more recent event).
 *
 * @component
 * @returns {JSX.Element|null} The dialog component or null if not open
 */
const JobStartAtDialogBase = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const nowMs = useContext(TickerContext);

  const company = useSelector(getCompany);
  const latestNonIdleInterval = useSelector(getLatestNonIdleInterval);
  const latestOperatorRun = useSelector(getLatestOperatorRun);
  const latestActivity = useSelector(getLatestActivity);

  // Get timezone from company
  const tzid = _get(company, 'timezone');

  // Extract raw values to satisfy exhaustive-deps rule
  const latestNonIdleIntervalStart = latestNonIdleInterval?.start;
  const latestActivityEnd = latestActivity?.end;
  const latestOperatorRunStartAt = latestOperatorRun?.startAt;

  /**
   * Memoized converted time values to avoid redundant timezone conversions
   * Only calculate when dialog is open for performance
   */
  const convertedTimes = useMemo(() => {
    return {
      lastActiveAt: safeConvertToISO(latestNonIdleIntervalStart, tzid),
      lastActivityAt: safeConvertToISO(latestActivityEnd, tzid),
      operatorRunStartAt: safeConvertToISO(latestOperatorRunStartAt, tzid),
    };
  }, [
    latestNonIdleIntervalStart,
    latestActivityEnd,
    latestOperatorRunStartAt,
    tzid,
  ]);

  /**
   * Generates the list of available start time options
   *
   * Four possible options:
   * 1. "Now" - Current time (always available)
   * 2. "Last active" - When machine was last active/unavailable
   * 3. "Since last job" - When the last job/activity ended
   * 4. "Since sign in" - When current operator session started
   *
   * Logic for options 3 & 4:
   * - Show "Since last job" when we have job end time AND it's more recent than sign-in
   * - Show "Since sign in" when we have sign-in time AND it's more recent than job end
   * - If only one timestamp exists, show that option
   * - If neither exists, show "Since sign in" as unavailable
   *
   * Options are disabled when:
   * - Data is unavailable
   * - Time would be before a more recent event (prevents logical inconsistencies)
   *
   * @returns {Array<Object>} Array of option objects with id, label, value, and disabled status
   */
  const optionsList = useMemo(() => {
    const { lastActiveAt, lastActivityAt, operatorRunStartAt } = convertedTimes;

    const options = [
      {
        id: 'now',
        label: t('Now'),
        value: toISO(nowMs),
        disabled: false,
      },
    ];

    // Option 2: "Last active" - always show, disable if unavailable or superseded by newer events
    if (lastActiveAt) {
      const isDisabled =
        safeIsBefore(lastActiveAt, lastActivityAt) ||
        safeIsBefore(lastActiveAt, operatorRunStartAt);

      options.push({
        id: 'last-active',
        label: t('Last active'),
        value: lastActiveAt,
        disabled: isDisabled,
      });
    } else {
      options.push({
        id: 'last-active',
        label: t('Last active'),
        value: t('Unavailable'),
        disabled: true,
      });
    }

    // Options 3 & 4: Determine which third option to show based on what's more recent
    const hasJobEnd = Boolean(lastActivityAt);
    const hasSignIn = Boolean(operatorRunStartAt);

    if (hasJobEnd && hasSignIn) {
      // Both available: show the more recent one
      const isJobEndMoreRecent = !safeIsBefore(
        lastActivityAt,
        operatorRunStartAt
      );

      if (isJobEndMoreRecent) {
        options.push({
          id: 'since-last-job',
          label: t('Since last job'),
          value: lastActivityAt,
          disabled: false,
        });
      } else {
        options.push({
          id: 'since-sign-in',
          label: t('Since sign in'),
          value: operatorRunStartAt,
          disabled: false,
        });
      }
    } else if (hasJobEnd) {
      options.push({
        id: 'since-last-job',
        label: t('Since last job'),
        value: lastActivityAt,
        disabled: false,
      });
    } else if (hasSignIn) {
      options.push({
        id: 'since-sign-in',
        label: t('Since sign in'),
        value: operatorRunStartAt,
        disabled: false,
      });
    } else {
      // Neither available
      options.push({
        id: 'since-sign-in',
        label: t('Since sign in'),
        value: t('Unavailable'),
        disabled: true,
      });
    }

    return options;
  }, [convertedTimes, t, nowMs]);

  /**
   * Closes the dialog and resets the event emitter
   */
  const closeJobStartAtDialog = useCallback(() => {
    dispatch(actionCloseJobStartAtDialog());
  }, [dispatch]);

  /**
   * Handles dialog close action
   * Emits cancel event and closes the dialog
   */
  const handleClose = useCallback(() => {
    JobStartAtDialogEventEmitter.fireAndForget('cancel');
    closeJobStartAtDialog();
  }, [closeJobStartAtDialog]);

  /**
   * Handles option selection
   * Emits success event with selected value and closes the dialog
   *
   * @param {string} selectedValue - The selected time value
   */
  const handleSelect = useCallback(
    (selectedValue) => {
      JobStartAtDialogEventEmitter.fireAndForget('success', selectedValue);
      closeJobStartAtDialog();
    },
    [closeJobStartAtDialog]
  );

  return (
    <BaseJobTimeDialog
      headerText={t('Select job start time')}
      optionsList={optionsList}
      tzid={tzid}
      onClose={handleClose}
      onSelect={handleSelect}
    />
  );
};

const JobStartAtDialog = () => {
  // Redux selectors for component state
  const isOpen = useSelector(getJobStartAtDialogOpen);

  if (!isOpen) return null;

  return <JobStartAtDialogBase />;
};

export default JobStartAtDialog;
